/* eslint-disable max-lines */
import React, { useState } from 'react';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import { useTranslate } from '@im/react-localization';
import {
  DataGrid,
  LoadingOverlay,
  NoRowsOverlay_2,
} from 'src/components/DataGrid';
import { GridColDef } from '@mui/x-data-grid';
import { Pagination } from '@im/ui-lib/Pagination';
import { LineDetails } from '../../hooks/deal-details';
import { StyledText, StyledTextSub } from 'src/components/StyledTypography';
import {
  OrderHistoryQueryRequest,
  useDownloadOrderHistory,
  useOrderHistoryQuery,
} from '../../hooks/order-history';
import { useDateTimeFormatter } from 'src/hooks/dateTimeFormatter';
import { downloadFile } from 'src/util/downloadFileUtil';
import useSnackBar from 'src/hooks/useSnackBar';

interface OrderHistoryTableDrawerProps {
  handleClose?: () => void;
  importreferenceid: number;
  lineDetails: LineDetails;
}

export default function OrderHistoryTableDrawer(
  props: Readonly<OrderHistoryTableDrawerProps>
) {
  const translate = useTranslate();
  const formatDateTime = useDateTimeFormatter();
  const { showSnackBar } = useSnackBar();

  const [pageData, setPageData] = useState<{
    page: number;
    pageSize: number;
  }>({ page: 1, pageSize: 10 });

  const requestBody: OrderHistoryQueryRequest = {
    discountTrackingId: String(props.lineDetails.dd_tracking),
    page: pageData.page,
    pageSize: pageData.pageSize,
  };

  const downloadOrderHistory = useDownloadOrderHistory(requestBody);

  const orderHistoryDetails = useOrderHistoryQuery(requestBody);

  const rows = orderHistoryDetails.data?.discountLines ?? [];

  const getColumns = (
    info: GridColDef<typeof rows[number]>
  ): GridColDef<typeof rows[number]> => ({
    align: info.align ?? 'left',
    cellClassName: 'cellvender',
    field: info.field,
    headerName: translate(info.headerName as string),
    renderCell:
      info?.renderCell ??
      ((item) => {
        const value = <StyledText noWrap>{item.value}</StyledText>;
        return <Tooltip title={value}>{value}</Tooltip>;
      }),
    sortable: info.sortable ?? false,
    width: info.width ?? 100,
  });

  const columns: GridColDef<typeof rows[number]>[] = [
    getColumns({
      field: 'orderNumber',
      headerName: 'VendorDeals.OrderNumber',
      width: 150,
    }),
    getColumns({
      field: 'orderCreated',
      headerName: 'VendorDeals.OrderCreatedDate',
      renderCell: (item) => (
        <Stack>
          <StyledText noWrap variant="bodycopyregular">
            {item.value ? formatDateTime(item.value, true) : '--'}
          </StyledText>
        </Stack>
      ),
      width: 200,
    }),
    getColumns({
      field: 'acopTrackingId',
      headerName: 'VendorDeals.AcopTrackingId',
      width: 150,
    }),
    getColumns({
      field: 'acopCreated',
      headerName: 'VendorDeals.AcopCreatedDate',
      renderCell: (item) => (
        <Stack>
          <StyledText noWrap variant="bodycopyregular">
            {item.value ? formatDateTime(item.value, true) : '--'}
          </StyledText>
        </Stack>
      ),
      width: 200,
    }),
    getColumns({
      field: 'orderLineNbr',
      headerName: 'VendorDeals.OrderLineNumber',
    }),
    getColumns({
      field: 'discountAcopId',
      headerName: 'VendorDeals.GDSTrackingNumber',
      width: 150,
    }),
    getColumns({
      field: 'sku',
      headerName: 'VendorDeals.SkuNumber',
    }),
    getColumns({
      field: 'changeType',
      headerName: 'VendorDeals.ChangeType',
      width: 150,
    }),
    getColumns({
      field: 'orderLineQuantity',
      headerName: 'VendorDeals.OrderLineQuantity',
    }),
    getColumns({
      field: 'orderShipQuantity',
      headerName: 'VendorDeals.OrderShipQuantity',
    }),
    getColumns({
      field: 'quantity',
      headerName: translate('VendorDeals.ProductQuantity'),
    }),
    getColumns({
      field: 'createdDate',
      headerName: 'Common.DateCreated',
      renderCell: (item) => (
        <Stack>
          <StyledText noWrap variant="bodycopyregular">
            {item.value && formatDateTime(item.value, true)}
          </StyledText>
        </Stack>
      ),
      width: 200,
    }),
    getColumns({
      field: 'createdBy',
      headerName: 'Common.CreatedBy',
      width: 200,
    }),
    getColumns({
      field: 'comment',
      headerName: 'Common.Comment',
      width: 200,
    }),
  ];

  const handleDownload = () => {
    showSnackBar({
      autoHide: 'stay',
      message: translate('Common.Downloading'),
      severity: 'info',
    });
    downloadOrderHistory
      .mutateAsync()
      .then((res) => {
        downloadFile(res, `OrderHistory-${props.lineDetails.vpn}.xlsx`);
        showSnackBar({
          autoHide: 2000,
          message: translate('Common.DownloadSuccess'),
          severity: 'success',
        });
      })
      .catch(() => {
        showSnackBar({
          autoHide: 2000,
          message: translate('Common.FileNotFound'),
          severity: 'error',
        });
      });
  };

  const renderPagination = () => (
    <Pagination
      data-testid="gds-vp-order-history-table-pagination"
      onPageChange={(page) => setPageData({ ...pageData, page })}
      onRowsPerPageChange={(pageSize) => setPageData({ ...pageData, pageSize })}
      page={pageData.page}
      rowsPerPage={pageData.pageSize}
      totalCount={orderHistoryDetails.data?.totalCount ?? 0}
    />
  );

  return (
    <Drawer
      anchor={'right'}
      data-testid="gds-vp-order-history-table-drawer"
      onClose={props.handleClose}
      open={Boolean(props.lineDetails)}
      sx={{
        '.MuiDrawer-paperAnchorRight': {
          height: `calc(100vh - 50px)`,
          top: 50,
          width: '80%',
        },
      }}
    >
      <Stack
        data-testid="gds-vp-order-history-drawer-header"
        gap={2}
        paddingTop={2}
        paddingX={2}
      >
        <Stack direction="row" justifyContent="space-between">
          <Typography
            data-testid="gds-vp-order-history-table-drawer-heading"
            fontSize={16}
            lineHeight={`22px`}
            sx={{ alignContent: 'center', display: 'grid' }}
            variant="smalltextsemibold"
          >
            {translate('VendorDeals.OrderHistoryVpnId', {
              id: props.lineDetails.vpn,
            })}
          </Typography>

          <IconButton
            onClick={props.handleClose}
            size="small"
            sx={{ alignSelf: 'baseline' }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
        <Divider />
        <Stack direction="row" gap={3}>
          <Stack>
            <Typography variant="bodycopysemibold">
              {props.lineDetails.dd_max_qty_limit}
            </Typography>
            <StyledTextSub>
              {translate('VendorDeals.MaxQuantityLimit')}
            </StyledTextSub>
          </Stack>
          <Divider orientation="vertical" />
          <Stack>
            <Typography variant="bodycopysemibold">
              {props.lineDetails.dd_used_qty}
            </Typography>
            <StyledTextSub>
              {translate('VendorDeals.QuantityUsed')}
            </StyledTextSub>
          </Stack>
        </Stack>
      </Stack>
      <Box overflow="auto" p={2}>
        <DataGrid
          autoHeight
          columnBuffer={columns.length}
          columns={columns}
          components={{
            LoadingOverlay: LoadingOverlay,
            NoRowsOverlay: () => NoRowsOverlay_2(5),
            Pagination: renderPagination,
          }}
          data-testid="gds-vp-order-history-table-drawer-dataGrid"
          disableColumnFilter
          disableColumnMenu
          disableColumnSelector
          disableSelectionOnClick
          getRowHeight={() => 'auto'}
          hideFooterPagination={
            orderHistoryDetails.data?.discountLines
              ? orderHistoryDetails.data.discountLines.length <= 0
              : true
          }
          loading={orderHistoryDetails.isLoading}
          rows={rows as any}
          sx={{
            '.MuiDataGrid-columnHeader--moving': {
              backgroundColor: 'transparent',
            },
            '.MuiDataGrid-columnHeaderTitle': {
              whiteSpace: 'normal',
            },
            '.MuiDataGrid-row': {
              minHeight: '52px !important',
            },
          }}
        />
      </Box>
      <Stack
        direction="row"
        justifyContent={'flex-end'}
        marginTop="auto"
        paddingX={`16px`}
        paddingY={`24px`}
        spacing={`12px`}
      >
        <Button
          data-testid="gds-vp-order-history-table-drawer-cancel-btn"
          onClick={props.handleClose}
          variant="newOutlined"
        >
          {translate('Common.Cancel')}
        </Button>
        <Button
          data-testid="gds-vp-order-history-table-drawer-download-order-history-btn"
          disabled={rows.length === 0}
          onClick={handleDownload}
          variant="newContained"
        >
          {translate('VendorDeals.DownloadOrderHistory')}
        </Button>
      </Stack>
    </Drawer>
  );
}
